import React, { Children, Component } from 'react';

import { FetchAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { ContainerProvider } from '@philips-emr/tws-core/lib/component';
import { DataContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, element, eventListener, intellisense, IntellisenseExpectedType, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { getPathParams, getResolvedPathAndParameters, isPathReady } from '@philips-emr/tws-core/lib/util';
import { getLostProps, isDefined, isEqualShallow, isFunction, isObject, propagateData } from '@philips-emr/tws-utils';

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.DATA_ENTRY,
  ComponentType.FORM,
  ComponentType.LIST,
  ComponentType.NAVIGATION,
];

@AwesomeComponent({ type: ComponentType.BEHAVIOR, api: FetchAPI })
class Fetch extends Component {

  static contextType = DataContext.Context;

  state = {
    done: false,
    forceFetch: false,
    lazy: false,
    limit: null,
    loading: false,
    page: null,
    params: {},
    accumulate: false,
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    endpoint: intellisense(string, IntellisenseExpectedType.URL),
    lazy: bool,
    onAbort: eventListener,
    onAfterFetch: eventListener,
    onBeforeFetch: eventListener,
    parameters: object.notEnumerable(),
    uuid: string,
  };

  static defaultProps = {
    endpoint: '/api/',
  };

  get sizeParameters() {
    return ['size'];
  }

  componentDidMount() {
    this.fetchLazy();
  }

  componentDidUpdate(previousProps, previousState) {
    if (this.shouldFetch(previousProps, previousState)) {
      this.fetch();
    }
  }

  componentWillUnmount() {
    const { abortController } = this.state;

    abortController && abortController.abort();
  }

  hasChildren = () => {
    const { children } = this.props;

    return Children.count(children) > 0;
  };

  isEndpointReady = () => {
    const { endpoint } = this.props;

    const requestParameters = this.getRequestParameters(this.props, this.state);
    const { resolvedPath: resolvedEndpoint } = getResolvedPathAndParameters(endpoint, requestParameters);

    return isPathReady(resolvedEndpoint);
  };

  shouldFetch = (prevProps, prevState) => {
    const { children, endpoint } = this.props;
    const { page, forceFetch, previousParameters } = this.state;

    const requestParameters = this.getRequestParameters(this.props, this.state);

    const isEndpointReady = this.isEndpointReady();

    if (forceFetch) {
      this.setState({
        forceFetch: false,
      });

      return isEndpointReady;
    }

    if (prevState.page !== page) {
      return isEndpointReady;
    }

    if (!isEqualShallow(previousParameters, requestParameters)) {
      return isEndpointReady;
    }

    if (prevProps.endpoint !== endpoint || prevProps.children !== children) {
      return isEndpointReady;
    }

    return false;
  };

  getCount() {
    const { data } = this.state;

    if (isObject(data)) {
      const keys = Object.keys(data);
      const key = keys.find((key) => this.sizeParameters.includes(key));

      if (key) {
        return data[key];
      }
    }

    return data ? data.length : null;
  }

  getParametersByName(names = [], parameters = {}) {
    return names.reduce((result, name) => (result[name] = parameters[name], result), {});
  }

  getRequestParameters(props, state) {
    const { endpoint, parameters = {} } = props;
    const { limit, page, params = {} } = state;

    const names = getPathParams(endpoint);
    const paramsFromContext = this.getParametersByName(names, this.context || {});

    const requestParams = {
      ...parameters,
      ...params,
    };

    isDefined(page) && page != null && (requestParams.page = page);
    isDefined(limit) && limit != null && (requestParams.limit = limit);

    return { ...paramsFromContext, ...requestParams };
  }

  getData(prevData, data, accumulate) {
    if (!isDefined(prevData) || !accumulate) {
      return data;
    }

    return Array.isArray(prevData)
      ? [...prevData, ...data]
      : { ...prevData, ...data };
  }

  fetch = () => {
    if (this.state.abortController) {
      this.state.abortController.abort();
    }

    if (!this.hasChildren() || !this.isEndpointReady()) {
      return;
    }

    const { endpoint, onBeforeFetch } = this.props;
    const requestParameters = this.getRequestParameters(this.props, this.state);

    const abortController = new AbortController();
    this.setState({
      abortController,
      loading: true,
      previousParameters: requestParameters,
    });

    isFunction(onBeforeFetch) && onBeforeFetch();

    HttpService.get(endpoint, requestParameters, { abortController })
      .then(this.handleSuccess)
      .catch(this.handleError);
  };

  handleSuccess = ({ body }) => {
    const { onAfterFetch } = this.props;

    this.setState(({ data: prevData, accumulate, limit }) => {
      const data = this.getData(prevData, body, accumulate);

      const done = Array.isArray(data) ? body.length < limit : true;

      return {
        abortController: null,
        data,
        done,
        loading: false,
      };
    }, () => {
      const { data } = this.state;

      isFunction(onAfterFetch) && onAfterFetch({ error: null, data });
    });
  };

  handleError = ({ statusText }) => {
    const { onAfterFetch } = this.props;

    this.setState({
      abortController: null,
      error: statusText,
      loading: false,
    }, () => {
      isFunction(onAfterFetch) && onAfterFetch({ data: null, error: statusText });
    });
  };

  fetchLazy() {
    const { lazy } = this.props;

    !lazy && this.fetch();
  }

  onPageChange = (options = {}) => {
    const { page, accumulate = false, params: newParams = {}, limit, mergeParams = false } = options;
    const { params: oldParams } = this.state;

    const params = mergeParams ? { ...oldParams, ...newParams } : newParams;

    this.setState({
      page, limit, accumulate, params,
    });
  }

  onSort = (options) => {
    const { direction, entry, limit, params } = options;

    const parameters = {
      ...params,
      entry,
      direction,
    };

    this.setState({
      limit,
      page: 0,
      params: parameters,
      accumulate: false,
    });
  };

  render() {
    const { data, loading, done, page } = this.state;
    const { children, uuid } = this.props;
    const lostProps = getLostProps(Fetch.propTypes, this.props);
    const Container = ContainerProvider.provide(this);

    // if (err) {
    //   return <div>Error: {err.message}</div>;
    // }

    if (React.Children.count(children) === 0) {
      return <Container acceptedTypes={acceptedTypes} className="u-expand" uuid={uuid} />;
    }

    const inherited = {
      ...lostProps,
      loading,
      done,
      page,
      onNextPage: this.onPageChange,
      onPrevPage: this.onPageChange,
      onSort: this.onSort,
      count: this.getCount(),
    };

    return propagateData(children, data, inherited);
  }
}

export default Fetch;
